import { Component, OnInit, Input, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, Inject, ElementRef, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Form, FormArray } from '@angular/forms';

import { Store } from '@ngrx/store';
import { firstValueFrom, lastValueFrom, Observable, of } from 'rxjs';
import { Globals } from 'src/app/common/globals';
import { take, filter, catchError, timeout } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { TranslateService } from '@ngx-translate/core';
import { HoneycombCustom } from 'src/app/services/honeycomb-api/honeycomb-custom-api';
import { MatSelectChange } from '@angular/material/select';
import { Honeycomb } from 'src/app/services/honeycomb-api/honeycomb-api';
import { AppState } from 'src/app/app.states';
import twotoneBorderColor from '@iconify/icons-ic/twotone-border-color';
import twotoneSwapVerticalCircle from '@iconify/icons-ic/twotone-swap-vertical-circle';
import twotoneCreateNewFolder from '@iconify/icons-ic/twotone-create-new-folder';
import twotoneDeleteForever from '@iconify/icons-ic/twotone-delete-forever';
import twotoneInfo from '@iconify/icons-ic/twotone-info';
import twotoneEdit from '@iconify/icons-ic/twotone-edit';
import roundDoneOutline from '@iconify/icons-ic/round-done-outline';
import twotoneImageSearch from '@iconify/icons-ic/twotone-image-search';
import twotoneAddAPhoto from '@iconify/icons-ic/twotone-add-a-photo';
import roundAddCircleOutline from '@iconify/icons-ic/round-add-circle-outline';
import twotoneIndeterminateCheckBox from '@iconify/icons-ic/twotone-indeterminate-check-box';
import twotoneCheckBox from '@iconify/icons-ic/twotone-check-box';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PromptDialogService } from '../../common/prompt-dialog/prompt-dialog.service';
import { TaskDetailComponent } from '../task-detail/task-detail.component';


@Component({
  selector: 'task-activities',
  templateUrl: './task-activities.component.html',
  styleUrls: ['./task-activities.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None
})
export class TaskActivitiesComponent implements OnInit, AfterViewInit {

  twotoneBorderColor = twotoneBorderColor;
  twotoneSwapVerticalCircle = twotoneSwapVerticalCircle;
  twotoneCreateNewFolder = twotoneCreateNewFolder;
  twotoneDeleteForever = twotoneDeleteForever;
  twotoneInfo = twotoneInfo;
  twotoneEdit = twotoneEdit;
  roundDoneOutline = roundDoneOutline;
  twotoneImageSearch = twotoneImageSearch;
  twotoneAddAPhoto = twotoneAddAPhoto;
  roundAddCircleOutline = roundAddCircleOutline;
  twotoneIndeterminateCheckBox = twotoneIndeterminateCheckBox;
  twotoneCheckBox = twotoneCheckBox;

  tenantHash: string = null;

  selectedStepper = 0;
  selectedActivity: number;
  inputUpdating = false;

  statuses$: Observable<Array<Honeycomb.Tenant.Tasker.IService.Model.Status>> = of([]);

  @Input() form: FormGroup;
  @Input() detailComponent: TaskDetailComponent;
  @Input() task: Honeycomb.Tenant.Tasker.IService.Model.Task;

  constructor(
    private formBuilder: FormBuilder,
    @Inject('InputController') private inputController: Honeycomb.Tenant.Tasker.IService.Controller.InputController,
    @Inject('TaskActivityController') private activityController: Honeycomb.Tenant.Tasker.IService.Controller.TaskActivityController,
    @Inject('FileControllerCustom') private fileController: HoneycombCustom.Tenant.Tasker.IService.Controller.FileControllerCustom,
    @Inject('TaskController') private taskController: Honeycomb.Tenant.Tasker.IService.Controller.TaskController,
    private globals: Globals,
    private store: Store<AppState>,
    private sanitizer: DomSanitizer,
    private cd: ChangeDetectorRef,
    private translate: TranslateService,
    private snackbar: MatSnackBar,
    private promptDialog: PromptDialogService
    ) {
      this.store.select(s => s.auth.tenantHash).subscribe(s => this.tenantHash = s);
  }

  async ngOnInit() {
    if (this.form.controls.taskActivitiesUpsert.value[0]) { // check if task has activities
      this.form.addControl('selectedActivity', this.formBuilder.control(this.form.controls.taskActivitiesUpsert.value[0].activityID));
    }

    this.form.controls.taskActivitiesUpsert.valueChanges
        .pipe(filter(t => !!t && t.length > 0), take(1))
        .subscribe(t => {
           this.form.controls.selectedActivity.setValue(t[0].activityID);
        });
  }

  ngAfterViewInit(): void {
  }

  getActivities(form) {
    return form.controls.taskActivitiesUpsert.controls;
  }

  getActivityInputs(taskActivity) {
    return taskActivity.controls.activityInputs.controls.sort((c, d) => c.value.sortingOrder - d.value.sortingOrder);
  }

  taskActivityStepSelected( $event: StepperSelectionEvent) {
    this.selectedStepper = $event.selectedIndex;
  }

  taskActivitySelectSelected( $event: MatSelectChange) {
    if (!$event.value) {
      this.selectedStepper = 0;
      return;
    }
    this.selectedStepper = this.form.controls.taskActivitiesUpsert.value.map(v => v.activityID).indexOf($event.value);
  }

  public get selectedActivityIndex(): number {
    return this.selectedStepper;
  }

  public set selectedActivityIndex(index: number) {
    this.selectedStepper = index;
  }

  public selectPrevActivity() {
    this.selectedStepper--;
    if (this.form.controls.selectedActivity) {
      this.form.controls.selectedActivity.setValue(this.selectedActivityObject.value.activityID);
    }
    this.cd.detectChanges();
  }

  public selectNextActivity() {
    this.selectedStepper++;
    if (this.form.controls.selectedActivity) {
      this.form.controls.selectedActivity.setValue(this.selectedActivityObject.value.activityID);
    }
    this.cd.detectChanges();
  }

  public get isLastActivity(): boolean {
    return this.selectedStepper === (this.getActivities(this.form).length - 1);
  }

  public get currentOf(): number[] {
    return [this.selectedStepper + 1, this.getActivities(this.form).length];
  }

  public get selectedActivityObject() {
    return this.getActivities(this.form)[this.selectedStepper];
  }

  public get areCurrentInputsFilled(): boolean {
    return (this.firstUnfilledInputCaption !== null);
  }

  public get firstUnfilledInputCaption(): string {
    if (this.taskActivityIsClosed(this.selectedActivityObject)) {
      return null;
    }
    const formGroups = this.getActivityInputs(this.selectedActivityObject) as FormGroup[];
    for (const fg of formGroups) {
      if (fg.value.required && fg.get('inputValue').value === null) {
        return fg.value.name;
      }
    }
    return null;
  }

  public setActivityClosed() {
    this.selectedActivityObject.value.activityDone = true;
  }

  taskActivityIsClosed(taskActivity) {
    return taskActivity.value.activityDone;
  }

  getActivityIndex(activityID: number) {
    return this.form.controls.taskActivitiesUpsert.value.map(v => v.activityID).indexOf(activityID);
  }

  getActivityNameByID(activityID: number) {
    return this.form.controls.taskActivitiesUpsert.value.find(v => v.activityID === activityID);
  }

  async noteUpdated($event: string, activityInput: FormGroup) {
    const taskActivityID = activityInput.parent.parent.value.taskActivityID;
    const meaningID = activityInput.value.meaningID;

    if (activityInput.value.isSnapshot) {
      const inputID = activityInput.value.inputID;
      await lastValueFrom(this.activityController.UpsertSnapshotNote({ inputID, note: $event }));
      await this.updateActivitySnapshotStatus(activityInput.parent.parent as FormGroup);
    } else {
      await lastValueFrom(this.activityController.UpsertNote({ taskActivityID, meaningID, note: $event }));
      await this.updateActivityStatus(activityInput.parent.parent as FormGroup);
    }
  }

  async valueUpdated($event: { newValue: any, origValue: any, resetDelegate: (oldVal) => void }, activityInput: FormGroup) {
    const taskActivityID = activityInput.parent.parent.value.taskActivityID;
    const meaningID = activityInput.value.meaningID;

    this.inputUpdating = true;
    this.cd.markForCheck();

    if (activityInput.value.isSnapshot) {
      const updatedSnapshotValue = await firstValueFrom(this.activityController
                                             .UpsertSnapshotValue({ 
                                               inputID: activityInput.value.inputID,
                                               value: $event.newValue
                                              })
      .pipe(
        catchError((e) =>
        {
          this.promptDialog.showErrorDialog(
               this.translate.instant('tasker.task-activities.update-failed'),
               this.translate.instant('tasker.task-activities.update-failed-title'));
          return of(null);
        }
      )));

      if (activityInput.value.uitype === Honeycomb.Common.Enums.UiType.file) {
        activityInput.patchValue({recordUid: (updatedSnapshotValue).value}, { emitEvent: false });
        activityInput.patchValue({valueUpdated: (updatedSnapshotValue).validFromUTC}, { emitEvent: false });
      } else {
        if (!!updatedSnapshotValue) {
          activityInput.patchValue({value: (updatedSnapshotValue).value}, { emitEvent: false });
        }
      }

      await this.updateActivitySnapshotStatus(activityInput.parent.parent as FormGroup);

    } else {
      const updatedValue = await lastValueFrom(this.activityController.UpsertValue({ taskActivityID, meaningID, value: $event.newValue })
      .pipe(
        catchError((e) =>
        {
          this.promptDialog.showErrorDialog(
               this.translate.instant('tasker.task-activities.update-failed'),
               this.translate.instant('tasker.task-activities.update-failed-title'));
          return of(null);
        }
      )));

      if (updatedValue === null) {
        activityInput.patchValue({inputValue: $event.origValue}, { emitEvent: false });
        $event.resetDelegate($event.origValue);
        this.cd.detectChanges();
        this.inputUpdating = false;
        return;
      }

      activityInput.patchValue({taskValueID: updatedValue.taskValueID}, { emitEvent: false });

      if (activityInput.value.uitype === Honeycomb.Common.Enums.UiType.file) {
        activityInput.patchValue({recordUid: (updatedValue).value}, { emitEvent: false });
        activityInput.patchValue({valueUpdated: (updatedValue).validFromUTC}, { emitEvent: false });
      } else {
        activityInput.patchValue({value: (updatedValue).value}, { emitEvent: false });
      }

      await this.updateActivityStatus(activityInput.parent.parent as FormGroup);
    }

    this.inputUpdating = false;
    this.cd.detectChanges();
  }

  async valuePhotoInsert($event: any, activityInput: FormGroup) {

    const taskActivityID = activityInput.parent.parent.value.taskActivityID;
    const meaningID = activityInput.value.meaningID;
    this.inputUpdating = true;
    this.cd.markForCheck();

    try {
      if (activityInput.value.isSnapshot) {
        const inputID = activityInput.value.inputID;
        const updatedValue = await lastValueFrom(this.activityController
        .ValuePhotoInsertSnapshot({ inputID, recordUid: $event})
        .pipe(
           timeout({ each: 60 * 1000 }),
           catchError((e) => { this.snackbar.open(
             this.translate.instant('tasker.input.update-failed'), null, { duration: 6000 });
                               return of(null);
           }
         )));

        if (updatedValue === null) {
          return;
        }

        const valPhotos = activityInput.get('taskValuePhotos').value;
        // replace tem value /w new one

        const ixToReplace = valPhotos.findIndex(v => v.recordUid === $event);
        if (ixToReplace !== -1) {
          valPhotos[ixToReplace].recordUid = null;
          valPhotos[ixToReplace].taskerFileUid  = updatedValue.taskerFileUid;
        }
        activityInput.patchValue({taskValuePhotos: valPhotos}, { emitEvent: false });

        this.updateActivitySnapshotStatus(activityInput.parent.parent as FormGroup);

      } else {
        const updatedValue = await firstValueFrom(this.activityController
        .ValuePhotoInsert({ meaningID, recordUid: $event, taskActivityID })
        .pipe(
           timeout({ each: 60 * 1000 }),
           catchError((e) => { this.snackbar.open(
             this.translate.instant('tasker.input.update-failed'), null, { duration: 6000 });
                               return of(null);
           }
         )));
        // .toPromise<Honeycomb.Tenant.Tasker.IService.Model.TaskValuePhoto>();
        if (updatedValue === null) {
          return;
        }
        const valPhotos = activityInput.get('taskValuePhotos').value;
        // replace tem value /w new one

        const ixToReplace = valPhotos.findIndex(v => v.recordUid === $event);
        if (ixToReplace !== -1) {
          valPhotos[ixToReplace] = updatedValue;
        }

        activityInput.patchValue({taskValueID: updatedValue.taskValueID}, { emitEvent: false });
        activityInput.patchValue({taskValuePhotos: valPhotos}, { emitEvent: false });

        this.updateActivityStatus(activityInput.parent.parent as FormGroup);
      }
    } finally {
      this.inputUpdating = false;
      this.cd.detectChanges();
    }
  }

  trackInputBy(index, input: FormGroup) {
    return input.value.activityInputID;
  }

  async updateActivityStatus(taFormGroup: FormGroup) {
    const prevValue = taFormGroup.get('activityDone').value;
    if (prevValue !== taFormGroup.valid) {
      taFormGroup.get('activityDone').setValue(taFormGroup.valid);
      var res = await firstValueFrom(this.activityController.TaskActivityStatus({
        activityDone: taFormGroup.valid,
        taskActivityID: taFormGroup.value.taskActivityID
      }));
      // NOTE: If snapshot has been created during status update (single activity, set 'finished' after first input update),
      // the whole model must be updated from DB (because now snapshot values have inputIDs)
      // otherwise new value won't be saved
      let isSnapshot = !!res.operationSnapshotID;
      if (isSnapshot) {
        this.inputUpdating = true;
        (<FormArray>taFormGroup.get('activityInputs')).controls.forEach(s => s.get('isSnapshot').disable({onlySelf: true, emitEvent: true}));
        this.cd.detectChanges();
        taFormGroup.get('isSnapshot').setValue(isSnapshot);
        (<FormArray>taFormGroup.get('activityInputs')).controls.forEach(s => s.get('isSnapshot').setValue(isSnapshot));
        let t = await this.detailComponent.refreshTask(this.form.get('taskID').value);
        await this.detailComponent.loadFormInternal(t, false);
        this.inputUpdating = false;
        (<FormArray>taFormGroup.get('activityInputs')).controls.forEach(s => s.get('isSnapshot').enable({onlySelf: true, emitEvent: false}));
        this.cd.detectChanges();
      }
    }
  }

  async updateActivitySnapshotStatus(taFormGroup: FormGroup) {
    const prevValue = taFormGroup.get('activityDone').value;
    if (prevValue !== taFormGroup.valid) {
      taFormGroup.get('activityDone').setValue(taFormGroup.valid);
      const res = await firstValueFrom(this.activityController.TaskActivitySnapshotStatus({
        activityDone: taFormGroup.valid,
        activityID: taFormGroup.value.activityID
      }));
      this.form.get("taskStatusID").setValue(res.taskStatusID);
    }
  }

  async valuePhotoRemove($event: any, activityInput: FormGroup) {
    if (!$event.taskValuePhotoID) {
      return;
    }

    if (activityInput.value.isSnapshot) {
      const updatedValue = await this.activityController.ValuePhotoSnapshotRemove($event.taskValuePhotoID).toPromise();
    } else {
      const updatedValue = await this.activityController.ValuePhotoRemove($event.taskValuePhotoID).toPromise();
    }

    let valPhotos = activityInput.get('taskValuePhotos').value;
    // replace item value /w new one

    valPhotos = valPhotos.filter(v => v.taskValuePhotoID !== $event.taskValuePhotoID);
    activityInput.patchValue({taskValuePhotos: valPhotos}, { emitEvent: false });
    this.cd.detectChanges();
  }
}
